import http from '@/service/http'
import { ROOT_SIMULATOR } from '@/service/modules/urlConfig'


export interface PilotAssessmentApiResponse<T> {
  code: number | string
  data: T
  message: string
}

export enum PilotAssessmentExamTimeStatus {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  ENDED = 'ENDED',
}

export interface PilotAssessmentHallSummary {
  publishedCount: number
  joinableCount: number
  myCompletedRoundCount: number
  personalResultAvailable: boolean
}

export type PilotAssessmentHallPermissionStatus = 'ALL' | 'CAN_JOIN' | 'CANNOT_JOIN'

export type PilotAssessmentHallUnavailableReason =
  | 'NOT_STARTED'
  | 'ENDED'
  | 'NO_PHONE'
  | 'NOT_IN_CANDIDATE_LIST'

export interface PilotAssessmentHallSubject {
  subjectCode: string
  subjectName: string
  weightPercent: number
}

export interface PilotAssessmentHallSession {
  sessionId: string | number
  sessionName: string
  participateMode: number
  publishTime: string
  validStartTime: string
  validEndTime: string
  examTimeStatus: PilotAssessmentExamTimeStatus
  qualified: boolean
  canEnter: boolean
  unavailableReason?: PilotAssessmentHallUnavailableReason | null
  enabledSubjects: PilotAssessmentHallSubject[]
}

export interface PilotAssessmentHallPage {
  records: PilotAssessmentHallSession[]
  total?: number
  totalCount?: number
  totalPage?: number
  pageNum?: number
  pageSize?: number
}

export interface PilotAssessmentHallPageParams {
  name?: string
  publishStartDate?: string
  publishEndDate?: string
  permissionStatus?: PilotAssessmentHallPermissionStatus
  pageNum: number
  pageSize: number
}

export interface PilotAssessmentSessionSubject {
  subjectCode: string
  subjectName: string
  fullScore: number
  passScore: number
  weightPercent: number
}

export interface PilotAssessmentSessionDetail {
  sessionId: string | number
  sessionName: string
  participateMode: number
  validStartTime: string
  validEndTime: string
  examTimeStatus: PilotAssessmentExamTimeStatus
  canEnter: boolean
  subjects?: PilotAssessmentSessionSubject[]
  enabledSubjects?: PilotAssessmentSessionSubject[]
  roundCount: number
  highestScore: number | string | null
  activeRoundId?: string | number | null
  activeRoundNo?: number | null
  completedSubjectCount: number
  totalSubjectCount: number
}

export interface StartOrResumePilotAssessmentRoundResult {
  roundId: string | number
  roundNo: number
  resumed: boolean
  completedSubjectCount: number
  totalSubjectCount: number
}

export enum PilotAssessmentRoundStatus {
  IN_PROGRESS = 0,
  COMPLETED = 1,
  INCOMPLETE = 2,
}

export enum PilotAssessmentRoundSubjectStatus {
  NOT_STARTED = 0,
  STARTING = 1,
  IN_PROGRESS = 2,
  PASSED = 3,
  FAILED = 4,
  INCOMPLETE = 5,
}

export type PilotAssessmentRoundSubjectStatusName =
  keyof typeof PilotAssessmentRoundSubjectStatus

export interface PilotAssessmentRoundSubject {
  subjectCode: string
  subjectName: string
  sceneTypeId: string | number
  subjectStatus: PilotAssessmentRoundSubjectStatus
  score?: number | string | null
  fullScore: number
  passScore: number
  weightPercent: number
}

export interface PilotAssessmentRoundDetail {
  roundId: string | number
  roundNo: number
  roundStatus: PilotAssessmentRoundStatus
  examTimeStatus?: PilotAssessmentExamTimeStatus
  sessionId: string | number
  sessionName: string
  completedSubjectCount: number
  totalSubjectCount: number
  subjects: PilotAssessmentRoundSubject[]
}

export interface StartPilotAssessmentSubjectParams {
  roundId: string | number
  subjectCode: string
  uavSpecificationId: string | number
  airLineId?: string | number
  windPower?: number
  windDirection?: string
  difficultyType?: number
  stationId?: string | number
}

export interface StartPilotAssessmentSubjectResult {
  roundSubjectId: string | number
  trainingId: string | number
  sceneId: string | number
  airLineId?: string | number | null
  stationId?: string | number | null
  subjectStatus: PilotAssessmentRoundSubjectStatusName
}

export interface FinishPilotAssessmentSubjectResult {
  roundSubjectId: string | number
  subjectCode: string
  subjectStatus: PilotAssessmentRoundSubjectStatusName
  score: number | string
  roundCompleted: boolean
  totalScore?: number | string | null
  certificateStatus: 'GENERATED' | 'NOT_GENERATED' | string
}

export interface PilotAssessmentResultSessionOption {
  sessionId: string | number
  sessionName: string
  completedRoundCount: number
  highestScore: number | string | null
}

export interface PilotAssessmentResultRound {
  roundId: string | number
  roundNo: number
  completeTime: string
  totalScore: number | string
  certificateStatus: 'GENERATED' | 'NOT_GENERATED' | string
}

export interface PilotAssessmentResultSubject {
  subjectCode: string
  subjectName: string
  subjectStatus: PilotAssessmentRoundSubjectStatusName
  trainingId?: string | number
  score: number | string
  fullScore: number
  passScore: number
  weightPercent: number
}

export interface PilotAssessmentResultDetail {
  roundId: string | number
  roundNo: number
  sessionId: string | number
  sessionName: string
  totalScore: number | string
  highestScore: number | string | null
  completeTime: string
  certificateStatus: 'GENERATED' | 'NOT_GENERATED' | string
  subjects: PilotAssessmentResultSubject[]
}

export interface PilotAssessmentCertificate {
  certificateNo: string
  sessionName: string
  roundNo: number
  totalScore: number | string
  completedDate: string
  generatedAt: string
}

// 获取考评大厅统计
export const getPilotAssessmentHallSummary = (
  params: object = {},
): Promise<PilotAssessmentApiResponse<PilotAssessmentHallSummary>> => {
  return http.post<PilotAssessmentHallSummary>(
    ROOT_SIMULATOR + '/api/pilotAssessment/hall/summary',
    params,
  )
}

// 分页获取考评大厅已发布场次
export const getPilotAssessmentHallPage = (
  params: PilotAssessmentHallPageParams,
): Promise<PilotAssessmentApiResponse<PilotAssessmentHallPage>> => {
  return http.post<PilotAssessmentHallPage>(
    ROOT_SIMULATOR + '/api/pilotAssessment/hall/page',
    params,
  )
}

// 获取考生场次详情
export const getPilotAssessmentSessionDetail = (
  params: { sessionId: string | number },
): Promise<PilotAssessmentApiResponse<PilotAssessmentSessionDetail>> => {
  return http.post<PilotAssessmentSessionDetail>(
    ROOT_SIMULATOR + '/api/pilotAssessment/session/detail',
    params,
  )
}

// 开始新轮次或继续当前活动轮次
export const startOrResumePilotAssessmentRound = (
  params: { sessionId: string | number },
): Promise<PilotAssessmentApiResponse<StartOrResumePilotAssessmentRoundResult>> => {
  return http.post<StartOrResumePilotAssessmentRoundResult>(
    ROOT_SIMULATOR + '/api/pilotAssessment/round/startOrResume',
    params,
  )
}

// 获取考评轮次详情
export const getPilotAssessmentRoundDetail = (
  params: { roundId: string | number },
): Promise<PilotAssessmentApiResponse<PilotAssessmentRoundDetail>> => {
  return http.post<PilotAssessmentRoundDetail>(
    ROOT_SIMULATOR + '/api/pilotAssessment/round/detail',
    params,
  )
}

// 开始或继续考评科目
export const startPilotAssessmentSubject = (
  params: StartPilotAssessmentSubjectParams,
): Promise<PilotAssessmentApiResponse<StartPilotAssessmentSubjectResult>> => {
  return http.post<StartPilotAssessmentSubjectResult>(
    ROOT_SIMULATOR + '/api/pilotAssessment/subject/start',
    params,
  )
}

// 异常结束考评科目
export const finishPilotAssessmentSubjectAbnormally = (
  params: { roundSubjectId: string | number },
): Promise<PilotAssessmentApiResponse<FinishPilotAssessmentSubjectResult>> => {
  return http.post<FinishPilotAssessmentSubjectResult>(
    ROOT_SIMULATOR + '/api/pilotAssessment/subject/finishAbnormally',
    params,
  )
}

// 获取个人成绩场次选项
export const getPilotAssessmentResultSessionOptions = (
  params: object = {},
): Promise<PilotAssessmentApiResponse<PilotAssessmentResultSessionOption[]>> => {
  return http.post<PilotAssessmentResultSessionOption[]>(
    ROOT_SIMULATOR + '/api/pilotAssessment/result/sessionOptions',
    params,
  )
}

// 获取场次的已完成轮次列表
export const getPilotAssessmentResultRoundList = (
  params: { sessionId: string | number },
): Promise<PilotAssessmentApiResponse<PilotAssessmentResultRound[]>> => {
  return http.post<PilotAssessmentResultRound[]>(
    ROOT_SIMULATOR + '/api/pilotAssessment/result/roundList',
    params,
  )
}

// 获取轮次成绩详情
export const getPilotAssessmentResultDetail = (
  params: { roundId: string | number },
): Promise<PilotAssessmentApiResponse<PilotAssessmentResultDetail>> => {
  return http.post<PilotAssessmentResultDetail>(
    ROOT_SIMULATOR + '/api/pilotAssessment/result/detail',
    params,
  )
}

// 获取专业技能证书
export const getPilotAssessmentCertificate = (
  params: { roundId: string | number },
): Promise<PilotAssessmentApiResponse<PilotAssessmentCertificate>> => {
  return http.post<PilotAssessmentCertificate>(
    ROOT_SIMULATOR + '/api/pilotAssessment/certificate/get',
    params,
  )
}
